/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListPackagesRequest,
  ListPackagesSecurity,
} from "../models/operations/listpackages.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListPackagesQuery,
  ListPackagesQueryData,
  prefetchListPackages,
  queryKeyListPackages,
} from "./listPackages.core.js";
export {
  buildListPackagesQuery,
  type ListPackagesQueryData,
  prefetchListPackages,
  queryKeyListPackages,
};

export type ListPackagesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listPackages packages
 *
 * @remarks
 * List all packages for a project.
 */
export function useListPackages(
  request?: ListPackagesRequest | undefined,
  security?: ListPackagesSecurity | undefined,
  options?: QueryHookOptions<ListPackagesQueryData, ListPackagesQueryError>,
): UseQueryResult<ListPackagesQueryData, ListPackagesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListPackagesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listPackages packages
 *
 * @remarks
 * List all packages for a project.
 */
export function useListPackagesSuspense(
  request?: ListPackagesRequest | undefined,
  security?: ListPackagesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListPackagesQueryData,
    ListPackagesQueryError
  >,
): UseSuspenseQueryResult<ListPackagesQueryData, ListPackagesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListPackagesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListPackagesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListPackagesQueryData,
): ListPackagesQueryData | undefined {
  const key = queryKeyListPackages(...queryKeyBase);

  return client.setQueryData<ListPackagesQueryData>(key, data);
}

export function invalidateListPackages(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "packages", "list", ...queryKeyBase],
  });
}

export function invalidateAllListPackages(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "packages", "list"],
  });
}
